import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';

import { isMobile } from '@common/device-manager';
import {
  is_ended_preorder_atom,
  isPreview,
  PdpCatalogDisplayStatus,
  PdpCatalogFulfillmentType,
  PdpCatalogSalesStatus,
  useCatalogProductIdParams,
  useIsAuthenticated,
  usePdpBaseInfo,
  usePdpConfig,
} from '@domains/pdp-common';

import { useBottomFloatingLog } from '../log/useBottomFloatingLog';
import { useNotchiPhoneSafeArea } from '../utils/notchiPhoneMediaQuery';

interface BottomFloatingContainerServiceProps {
  onClick: () => void;
}

export const useBottomFloatingContainerService = ({ onClick }: BottomFloatingContainerServiceProps) => {
  const { asPath } = useRouter();
  const { funnelType, isZigzagApp, source } = usePdpConfig();
  const is_preview = isPreview(asPath);

  const { clickPurchaseButtonUBL, clickPurchaseKakaoButtonUBL } = useBottomFloatingLog();
  const { safe_area_inset_bottom } = useNotchiPhoneSafeArea(isZigzagApp);

  const isMobileNaverEp = isMobile() && source?.includes('shopping_naver_all');
  const { isLoggedIn } = useIsAuthenticated();
  const showPurchaseWithKakao = isMobileNaverEp && !isZigzagApp && !isLoggedIn;

  const params = useCatalogProductIdParams();

  const {
    product,
    shop,
    key_color: { buy_button },
  } = usePdpBaseInfo(params);

  const is_raffle = product.trait_list?.find((o) => o.type === 'RAFFLE');

  const { id, product_url } = product;
  const isSample = product.trait_list?.some(({ type }) => type === 'SAMPLE');
  const is_ended_preorder = useAtomValue(is_ended_preorder_atom);
  const country = 'KR';
  const {
    checkoutClickUBL: pdpCheckoutClickUBL,
    pageviewUBL,
    clickPurchaseForProductPreviewUBL,
  } = useBottomFloatingLog();

  const isPurchasable = useMemo(() => {
    const is_sold_out = product.sales_status === PdpCatalogSalesStatus.SOLD_OUT;
    const is_on_sale = product.sales_status === PdpCatalogSalesStatus.ON_SALE;
    const is_hidden = is_raffle;

    if (is_hidden) {
      return false;
    }

    // 프리오더 상품이고, 프리오더 기간이 지났다면 disabled!
    if (product.preorder) {
      return is_on_sale && !is_ended_preorder;
    }

    // MEMO: 직진배송일 경우에는 재입고알림 기능이 있기때문에, 품절일 경우에도 구매하기 버튼을 활성화 하여 옵션선택창을 볼 수 있도록 한다.
    // MEMO2: 배송국가가 한국일때만 직진배송이 오픈되므로 해당 조건 추가 (country === 'KR')
    if (product.fulfillment_type === PdpCatalogFulfillmentType.ZIGZIN && country === 'KR') {
      return is_sold_out || is_on_sale;
    }

    return is_on_sale;
  }, [product, is_ended_preorder, country, is_raffle]);

  const renderButtonContents = () => {
    // 샘플 상품일 경우(현재는 한국 대상 뷰티관에서만 사용)
    if (isSample) {
      return '샘플 신청하기';
    }

    // 프리오더는 현재 한국 대상으로만 진행 (디국어 처리 x)
    if (product.preorder) {
      if (product.sales_status === PdpCatalogSalesStatus.SUSPENDED || is_ended_preorder) {
        return '판매 중단';
      } else if (product.sales_status === PdpCatalogSalesStatus.SOLD_OUT) {
        return '품절';
      } else if (product.sales_status === PdpCatalogSalesStatus.ON_SALE) {
        return '예약 구매하기';
      } else if (product.sales_status === PdpCatalogSalesStatus.PREPARING) {
        return '판매 예정';
      }
    }

    if (product.sales_status === PdpCatalogSalesStatus.PREPARING) {
      return '판매 예정';
    }
    if (
      product.sales_status === PdpCatalogSalesStatus.SOLD_OUT &&
      product.fulfillment_type !== PdpCatalogFulfillmentType.ZIGZIN
    ) {
      return '품절';
    }

    if (product.sales_status === PdpCatalogSalesStatus.SUSPENDED) {
      return '판매 중단';
    }

    return '구매하기';
  };

  const onClickPurchaseButton = () => {
    clickPurchaseButtonUBL(product_url, id, funnelType);

    if (!isZigzagApp) {
      pdpCheckoutClickUBL(id);
      pageviewUBL(id);
    }

    onClick();
  };

  const onClickPurchaseKakaoButton = () => {
    clickPurchaseKakaoButtonUBL(product_url, id, funnelType);
    pageviewUBL(id);
    onClick();
  };

  const onClickPurchaseForProductPreview = () => {
    clickPurchaseForProductPreviewUBL(id, funnelType, product.id, shop.name);
    onClick();
  };

  return {
    product_id: id,
    shop_id: shop.id,
    is_preview,
    isPurchasable: country === 'KR' ? isPurchasable : false,
    buyButton: buy_button,
    safe_area_inset_bottom,
    showPurchaseWithKakao,
    onClickPurchaseButton,
    onClickPurchaseKakaoButton,
    onClickPurchaseForProductPreview,
    renderButtonContents,
  };
};
